<template>
  <div style="background-color: #8fa0a8; min-height: 100vh; padding: 20px;">
    <v-container>
      <!-- Header with Back Button -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="header-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-btn icon @click="$router.push('/AdminSection')" class="mr-3">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <span class="montserrat-title">Password Reset Requests</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="loadRequests" :loading="loading">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stats Cards -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="40" color="orange">mdi-clock-alert</v-icon>
              <div class="stat-number">{{ pendingCount }}</div>
              <div class="stat-label">Pending Requests</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="40" color="green">mdi-check-circle</v-icon>
              <div class="stat-number">{{ completedCount }}</div>
              <div class="stat-label">Completed</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="40" color="primary">mdi-email</v-icon>
              <div class="stat-number">{{ totalCount }}</div>
              <div class="stat-label">Total Requests</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="40" color="blue">mdi-calendar-today</v-icon>
              <div class="stat-number">{{ todayCount }}</div>
              <div class="stat-label">Today</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Requests Table -->
      <v-card elevation="2">
        <v-card-title>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search by email"
            single-line
            hide-details
            clearable
            class="mb-4"
          ></v-text-field>
        </v-card-title>
        
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="requests"
            :loading="loading"
            :search="search"
            class="elevation-1"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, -1]
            }"
          >
            <template v-slot:item.requestedAt="{ item }">
              <span>{{ formatDate(item.requestedAt) }}</span>
            </template>
            
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" dark small>
                <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                {{ item.status }}
              </v-chip>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.status === 'pending'"
                color="primary"
                small
                @click="openPasswordDialog(item)"
              >
                <v-icon left small>mdi-lock-reset</v-icon>
                Set Password
              </v-btn>
              <v-chip v-else color="green" small dark>
                <v-icon left small>mdi-check</v-icon>
                Completed
              </v-chip>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-4">
                <v-icon size="64" color="grey">mdi-inbox</v-icon>
                <div class="text-h6 grey--text mt-2">No password reset requests</div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Set Password Dialog -->
    <v-dialog v-model="passwordDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline red--text">
          <v-icon left color="red">mdi-lock-reset</v-icon>
          Set New Password
        </v-card-title>
        
        <v-card-subtitle class="mt-2">
          <strong>User:</strong> {{ selectedRequest?.userEmail }}
        </v-card-subtitle>
        
        <v-card-text class="mt-4">
          <v-alert type="info" dense outlined class="mb-4">
            Password must be at least 6 characters long
          </v-alert>
          
          <v-text-field
            v-model="newPassword"
            label="New Password"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showPassword = !showPassword"
            outlined
            dense
            :rules="[rules.required, rules.minLength]"
          ></v-text-field>
          
          <v-text-field
            v-model="confirmPassword"
            label="Confirm Password"
            type="password"
            outlined
            dense
            :rules="[rules.required, rules.match]"
            :error-messages="passwordMatchError"
          ></v-text-field>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePasswordDialog">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            @click="setUserPassword" 
            :loading="settingPassword"
            :disabled="!isPasswordValid"
          >
            <v-icon left>mdi-check</v-icon>
            Set Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { 
  collection, 
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  addDoc
} from "firebase/firestore";
import { firebaseApp, db, auth } from "@/firebaseConfig";

export default {
  data() {
    return {
      requests: [],
      loading: false,
      passwordDialog: false,
      selectedRequest: null,
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
      settingPassword: false,
      search: '',
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      headers: [
        { text: 'Email', value: 'userEmail', sortable: true },
        { text: 'Requested At', value: 'requestedAt', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ],
      rules: {
        required: value => !!value || 'Required',
        minLength: value => value.length >= 6 || 'Must be at least 6 characters',
        match: () => this.newPassword === this.confirmPassword || 'Passwords must match'
      }
    };
  },
  
  computed: {
    pendingCount() {
      return this.requests.filter(r => r.status === 'pending').length;
    },
    completedCount() {
      return this.requests.filter(r => r.status === 'completed').length;
    },
    totalCount() {
      return this.requests.length;
    },
    todayCount() {
      const today = new Date().toDateString();
      return this.requests.filter(r => {
        if (!r.requestedAt) return false;
        const requestDate = new Date(r.requestedAt.seconds * 1000).toDateString();
        return requestDate === today;
      }).length;
    },
    isPasswordValid() {
      return this.newPassword && 
             this.confirmPassword && 
             this.newPassword === this.confirmPassword && 
             this.newPassword.length >= 6;
    },
    passwordMatchError() {
      if (this.confirmPassword && this.newPassword !== this.confirmPassword) {
        return ['Passwords do not match'];
      }
      return [];
    }
  },
  
  mounted() {
    this.loadRequests();
  },
  
  methods: {
    async loadRequests() {
      this.loading = true;
      try {
        const q = query(
          collection(db, 'passwordResetRequests'),
          orderBy('requestedAt', 'desc')
        );
        
        const snapshot = await getDocs(q);
        this.requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error loading requests:', error);
        this.showSnackbar('Error loading password reset requests', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    openPasswordDialog(request) {
      this.selectedRequest = request;
      this.newPassword = '';
      this.confirmPassword = '';
      this.passwordDialog = true;
    },
    
    closePasswordDialog() {
      this.passwordDialog = false;
      this.newPassword = '';
      this.confirmPassword = '';
      this.selectedRequest = null;
    },
    
    async setUserPassword() {
      if (!this.isPasswordValid) {
        this.showSnackbar('Please enter valid matching passwords', 'error');
        return;
      }
      
      this.settingPassword = true;
      
      try {
        // Create a password reset document that backend will process
        await addDoc(collection(db, 'adminPasswordResets'), {
          targetEmail: this.selectedRequest.userEmail,
          newPassword: this.newPassword,
          requestedBy: auth.currentUser.email,
          requestedAt: new Date(),
          status: 'pending'
        });
        
        // Update request status
        await updateDoc(doc(db, 'passwordResetRequests', this.selectedRequest.id), {
          status: 'completed',
          handledBy: auth.currentUser.email,
          newPasswordSet: true,
          completedAt: new Date()
        });
        
        this.showSnackbar('Password reset initiated successfully!', 'success');
        this.closePasswordDialog();
        this.loadRequests();
        
      } catch (error) {
        console.error('Error:', error);
        this.showSnackbar('Error: ' + error.message, 'error');
      } finally {
        this.settingPassword = false;
      }
    },
    
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusColor(status) {
      return status === 'pending' ? 'orange' : 'green';
    },
    
    getStatusIcon(status) {
      return status === 'pending' ? 'mdi-clock-alert' : 'mdi-check-circle';
    },
    
    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.montserrat-title {
  font-weight: 700;
  font-size: 1.5rem;
  color: #2c3e50;
}

.header-card {
  border-radius: 12px;
  background: white;
}

.stat-card {
  border-radius: 12px;
  background: white;
  height: 100%;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-top: 8px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: 4px;
}

.v-card {
  border-radius: 12px;
}

.v-data-table {
  border-radius: 8px;
}

.v-chip {
  font-weight: 600;
}

@media (max-width: 600px) {
  .montserrat-title {
    font-size: 1.2rem;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
}
</style>